import React, { useEffect, useRef } from 'react';
import { IStorefrontBundle } from '../../Api/Models/Products';
import { IBundleSelectionItem } from '../../Api/Models/Cart';
import { useEstimateBundlePriceMutation } from '../../Api/Api';

// Keep only digits — shared by the bundle-level and per-item quantity inputs.
export const stripNonNumeric = (value: string): string => value.replace(/[^0-9]/g, '');

export function buildDefaultBundleSelection(bundle: IStorefrontBundle): IBundleSelectionItem[] {
    const items: IBundleSelectionItem[] = [];

    const groupIds = new Set(bundle.groups?.map(g => g.id) ?? []);

    // The saved-search item is always required and pinned first — include it up front.
    const savedSearch = bundle.products.find(p => p.savedSearchId);
    if (savedSearch) {
        items.push({ productId: savedSearch.id, quantity: savedSearch.quantity, groupId: null, savedSearchId: savedSearch.savedSearchId });
    }

    bundle.groups?.forEach(group => {
        const chosen = bundle.products
            .filter(p => p.optionality === group.id)
            .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))[0];
        if (chosen) items.push({ productId: chosen.id, quantity: chosen.quantity, groupId: group.id });
    });

    bundle.products
        .filter(p => !p.savedSearchId && !groupIds.has(p.optionality ?? '') && p.optionality !== 'Optional')
        .forEach(p => items.push({ productId: p.id, quantity: p.quantity, groupId: null }));

    return items;
}

export interface IBundleInitialProduct {
    productId: string;
    quantity: number;
}

export interface IUseBundleConfigurationOptions {
    initialProducts?: IBundleInitialProduct[];
    bundleQuantity?: number;
    // Product ids that the rep pre-selected on a prepared cart. Pass undefined / [] for full edit.
    lockedProductIds?: string[];
    minItemQuantities?: Record<string, number>;
}

export interface IBundleConfiguration {
    selectedGroupProducts: Record<string, string>;
    setSelectedGroupProducts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    optionalSelections: Record<string, boolean>;
    setOptionalSelections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    itemQuantities: Record<string, number>;
    itemTmpQuantities: Record<string, string>;
    trySetItemTmpQuantity: (productId: string, value: string) => void;
    blurItemQuantity: (productId: string) => void;
    nonGroupedProducts: IStorefrontBundle['products'];
    // Server-driven estimated price for the current configuration
    estimatedPrice: number | null;
    isEstimating: boolean;
    estimateMessages: Record<string, string>;
    bundleSelection: IBundleSelectionItem[];
    isSelectionLocked: (productId: string) => boolean;
    itemQuantityWarnings: Record<string, string>;
}

/**
 * Owns the bundle configuration state (grouped choices, optional inclusions, per-item quantities)
 * and the derived price / selection payload. Extracted from BundlePage so the same logic backs the
 * bundle page and the cart edit panel. Pass `initialProducts` to seed from an existing cart line.
 *
 * Price is now server-driven: any selection/optional/quantity change triggers a debounced call to
 * POST /bundles/{bundleId}/estimate. The hook also fires the estimate immediately after the initial
 * configuration state settles so the base price shown reflects the minimum/default configuration.
 */
export function useBundleConfiguration(
    bundle: IStorefrontBundle | undefined,
    opts?: IUseBundleConfigurationOptions
): IBundleConfiguration {
    const initialProducts = opts?.initialProducts;
    const bundleQuantity = opts?.bundleQuantity ?? 1;

    // Build a stable Set from the caller-supplied locked ids (empty when not provided).
    const lockedSet = React.useMemo(
        () => new Set(opts?.lockedProductIds ?? []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(opts?.lockedProductIds)]
    );
    const isSelectionLocked = React.useCallback(
        (productId: string) => lockedSet.has(productId),
        [lockedSet]
    );

    const minQtyMap = opts?.minItemQuantities ?? {};

    const [selectedGroupProducts, setSelectedGroupProducts] = React.useState<Record<string, string>>({});
    const [optionalSelections, setOptionalSelections] = React.useState<Record<string, boolean>>({});
    // Per-item quantities for products whose changeQuantity flag is set. Committed values feed the
    // price/selection calculations; the tmp strings back the inputs while the shopper is typing.
    const [itemQuantities, setItemQuantities] = React.useState<Record<string, number>>({});
    const [itemTmpQuantities, setItemTmpQuantities] = React.useState<Record<string, string>>({});

    const [itemQuantityWarnings, setItemQuantityWarnings] = React.useState<Record<string, string>>({});

    // Server-driven estimated price state.
    const [estimatedPrice, setEstimatedPrice] = React.useState<number | null>(null);
    const [isEstimating, setIsEstimating] = React.useState(false);
    // Messages from the last estimate, keyed by product id (e.g. insufficient stock). Only populated
    // when the estimate returns no price; surfaced above the item tables.
    const [estimateMessages, setEstimateMessages] = React.useState<Record<string, string>>({});

    const [estimateBundlePrice] = useEstimateBundlePriceMutation();

    // Track whether the selection state has been initialized from the bundle definition yet,
    // so we can fire the initial estimate after the first initialization settles.
    const initializedRef = useRef(false);
    // Debounce timer ref.
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!bundle) return;

        // Grouped choices: prefer the group product present in the seed, else the default/first.
        const groupSelections: Record<string, string> = {};
        if (bundle.groups) {
            bundle.groups.forEach(group => {
                const groupProducts = bundle.products
                    .filter(p => p.optionality === group.id)
                    .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
                const seeded = initialProducts
                    ? groupProducts.find(p => initialProducts.some(ip => ip.productId === p.id))
                    : undefined;
                const chosen = seeded ?? groupProducts[0];
                if (chosen) groupSelections[group.id] = chosen.id;
            });
        }
        setSelectedGroupProducts(groupSelections);

        // Optional items: seeded inclusion when a seed is present, otherwise excluded by default.
        const optionals: Record<string, boolean> = {};
        bundle.products
            .filter(p => p.optionality === 'Optional')
            .forEach(p => {
                optionals[p.id] = initialProducts ? initialProducts.some(ip => ip.productId === p.id) : false;
            });
        setOptionalSelections(optionals);

        // Quantities: seeded value where present, else the product default.
        const quantities: Record<string, number> = {};
        const tmpQuantities: Record<string, string> = {};
        bundle.products.forEach(p => {
            const seededQty = initialProducts?.find(ip => ip.productId === p.id)?.quantity;
            const qty = seededQty ?? p.quantity;
            quantities[p.id] = qty;
            tmpQuantities[p.id] = qty.toString();
        });
        setItemQuantities(quantities);
        setItemTmpQuantities(tmpQuantities);

        // Mark as initialized so the selection-change effect fires the initial estimate.
        initializedRef.current = true;
    }, [bundle, initialProducts]);

    // Per-item quantity editing (products with changeQuantity === true). Mirrors the overall bundle
    // quantity field: keep only digits while typing, then commit a clamped value (min 1) on blur.
    const trySetItemTmpQuantity = (productId: string, value: string) => {
        setItemTmpQuantities(prev => ({ ...prev, [productId]: stripNonNumeric(value) }));
    };

    const blurItemQuantity = (productId: string) => {
        const floor = Math.max(1, minQtyMap[productId] ?? 1);
        let parsedQuantity = parseInt(itemTmpQuantities[productId] ?? '');
        if (!isNaN(parsedQuantity) && parsedQuantity < floor) {
            setItemQuantityWarnings(prev => ({ ...prev, [productId]:
                `This item is preconfigured and cannot be reduced below ${floor}.` }));
        } else {
            setItemQuantityWarnings(prev => { const next = { ...prev }; delete next[productId]; return next; });
        }
        if (isNaN(parsedQuantity) || parsedQuantity < floor) { parsedQuantity = floor; }
        setItemQuantities(prev => ({ ...prev, [productId]: parsedQuantity }));
        setItemTmpQuantities(prev => ({ ...prev, [productId]: parsedQuantity.toString() }));
    };

    const nonGroupedProducts = React.useMemo(() => {
        if (!bundle) return [];
        const gids = new Set(bundle.groups?.map(g => g.id) ?? []);
        // The saved-search item is rendered separately (pinned above groups), so exclude it here.
        return bundle.products.filter(p => !p.savedSearchId && !gids.has(p.optionality ?? ''));
    }, [bundle]);

    // Build the flat selection list from current state. Shared by the debounced estimate and the
    // cart API payload (bundleSelection returned from the hook). Locked items are always re-included
    // so they cannot be silently dropped even if state was initialized before the locked set arrived.
    const bundleSelection = React.useMemo((): IBundleSelectionItem[] => {
        if (!bundle) return [];

        const items: IBundleSelectionItem[] = [];

        // The saved-search item is always required and pinned first.
        const savedSearch = bundle.products.find(p => p.savedSearchId);
        if (savedSearch) {
            items.push({
                productId: savedSearch.id,
                quantity: itemQuantities[savedSearch.id] ?? savedSearch.quantity,
                groupId: null,
                savedSearchId: savedSearch.savedSearchId,
            });
        }

        bundle.groups?.forEach(group => {
            const pid = selectedGroupProducts[group.id];
            if (!pid) return;
            const product = bundle.products.find(p => p.id === pid && p.optionality === group.id);
            if (product) {
                items.push({
                    productId: pid,
                    quantity: itemQuantities[product.id] ?? product.quantity,
                    groupId: group.id,
                });
            }
        });

        nonGroupedProducts.forEach(product => {
            // A locked optional is always included; otherwise honour the checkbox state.
            const isIncluded = product.optionality === 'Optional'
                ? (lockedSet.has(product.id) || (optionalSelections[product.id] ?? false))
                : true;
            if (isIncluded) {
                items.push({
                    productId: product.id,
                    quantity: itemQuantities[product.id] ?? product.quantity,
                    groupId: null,
                });
            }
        });

        return items;
    }, [bundle, selectedGroupProducts, optionalSelections, nonGroupedProducts, itemQuantities, lockedSet]);

    // Fire a debounced estimate whenever the selection changes (including on initial load).
    // We depend on bundleSelection (memoized) + bundleQuantity so we don't need to re-list the
    // individual pieces of state.
    useEffect(() => {
        if (!bundle || !initializedRef.current) return;

        // Cancel any pending debounce.
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        setIsEstimating(true);
        debounceTimerRef.current = setTimeout(async () => {
            try {
                const result = await estimateBundlePrice({
                    bundleId: bundle.id,
                    selection: bundleSelection,
                    quantity: bundleQuantity,
                });
                if ('data' in result && result.data) {
                    // price may be null if CanSeePricing=false; keep null in that case.
                    setEstimatedPrice(result.data.price ?? null);
                    // messages is populated (keyed by product id) only when the configuration is not
                    // fully priceable — chiefly when a per-item quantity exceeds available stock.
                    // An empty/absent map clears any stale warnings.
                    setEstimateMessages(result.data.messages ?? {});
                }
            } catch {
                // On error, leave the last good price in place rather than blanking the UI.
            } finally {
                setIsEstimating(false);
            }
        }, 250);

        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundle, bundleSelection, bundleQuantity]);

    return {
        selectedGroupProducts,
        setSelectedGroupProducts,
        optionalSelections,
        setOptionalSelections,
        itemQuantities,
        itemTmpQuantities,
        trySetItemTmpQuantity,
        blurItemQuantity,
        nonGroupedProducts,
        estimatedPrice,
        isEstimating,
        estimateMessages,
        bundleSelection,
        isSelectionLocked,
        itemQuantityWarnings,
    };
}
